import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DialogContent } from '@biome-design-system/ui/DialogContent';
import { DialogActions } from '@biome-design-system/ui/DialogActions';
import { DialogTitle } from '@biome-design-system/ui/DialogTitle';
import { IconButton } from '@biome-design-system/ui/IconButton';
import { CloseIcon } from '@biome-design-system/icons/CloseIcon';
import { Button } from '@biome-design-system/ui/Button';
import { defineMessages, FormattedMessage, useIntl } from '@kyruus/intl';
import AppointmentInfo from './appointment-info';
import {
  AppointmentInfoContainer,
  AvailabilityModalHeader,
  AvailabilityDialog
} from './styles';
import sharedMessages from '../../shared/AvailabilityControls/messages';

const messages = defineMessages({
  availabilityModalConfirm: {
    id: 'availabilitymodal.buttontext.confirm',
    description: 'Button text for availability-modal "confirm" button.',
    defaultMessage: 'OK'
  },
  availabilityModalCancel: {
    id: 'availabilitymodal.buttontext.cancel',
    description: 'Button text for availability-modal "cancel" button.',
    defaultMessage: 'Cancel'
  },
  closeAvailabilityModalAriaLabel: {
    id: 'availability_modal.close.aria_label',
    description: 'Close modal icon button',
    defaultMessage: 'close'
  }
});

const ModalBodyApptInfo = ({
  patientRel,
  purpose,
  onPatientRelSelect,
  onSelectPurpose,
  patientRelOptions,
  providerDisplayName,
  providerPhone,
  availabilityButtonsConfig,
  isPurposeLoading,
  log,
  purposeHasNoAvailability,
  patientRelHasNoAvailability,
  hidePatientRel,
  apptPurposeFilterProps,
  apptInfoFormName
}) => (
  <AppointmentInfoContainer data-testid="AvailabilityModal__body">
    <AppointmentInfo
      isPurposeLoading={isPurposeLoading}
      patientRelOptions={patientRelOptions}
      patientRel={patientRel}
      purpose={purpose}
      onPatientRelSelect={onPatientRelSelect}
      onSelectPurpose={onSelectPurpose}
      providerDisplayName={providerDisplayName}
      providerPhone={providerPhone}
      availabilityButtonsConfig={availabilityButtonsConfig}
      log={log}
      purposeHasNoAvailability={purposeHasNoAvailability}
      patientRelHasNoAvailability={patientRelHasNoAvailability}
      hidePatientRel={hidePatientRel}
      apptPurposeFilterProps={apptPurposeFilterProps}
      formName={apptInfoFormName}
    />
  </AppointmentInfoContainer>
);

function ModalContainer(props) {
  const {
    patientRel,
    purpose,
    onPatientRelSelect,
    onSelectPurpose,
    isOpen,
    handleCloseModal,
    handleConfirmModal,
    patientRelOptions,
    providerDisplayName,
    providerPhone,
    availabilityButtonsConfig,
    isPurposeLoading,
    log,
    purposeHasNoAvailability,
    patientRelHasNoAvailability,
    hidePatientRel,
    apptPurposeFilterProps
  } = props;
  const intl = useIntl();
  const [shouldRenderModalFullscreen, setShouldRenderModalFullscreen] =
    useState(false);

  useEffect(() => {
    if (window && window.matchMedia) {
      const mq = window.matchMedia('(max-width: 420px)');

      // check on mount if the screen width indicates the modal needs to render fullscreen
      setShouldRenderModalFullscreen(mq.matches);

      // otherwise, listen for changes to screen width and update component state accordingly
      mq.addListener(({ matches }) => {
        setShouldRenderModalFullscreen(matches);
      });
    }
  }, []);

  const apptInfoFormName = 'appointment-info';
  return (
    <AvailabilityDialog
      maxWidth={'md'}
      open={isOpen}
      onClose={() => handleCloseModal('close_modal')}
      fullScreen={shouldRenderModalFullscreen}
      scroll="paper"
      slotProps={{
        paper: {
          sx: {
            mb: 4
          }
        }
      }}
    >
      <AvailabilityModalHeader>
        <DialogTitle className="sr-only">
          {intl.formatMessage(sharedMessages.globalAvailApptTypeButton)}
        </DialogTitle>
        <IconButton
          aria-label={intl.formatMessage(
            messages.closeAvailabilityModalAriaLabel
          )}
          color="neutral"
          onClick={() => handleCloseModal('close_modal')}
          size="medium"
          sx={{}}
          variant="text"
        >
          <CloseIcon />
        </IconButton>
      </AvailabilityModalHeader>
      <DialogContent dividers={true}>
        <ModalBodyApptInfo
          patientRel={patientRel}
          onPatientRelSelect={onPatientRelSelect}
          onSelectPurpose={onSelectPurpose}
          purpose={purpose}
          patientRelOptions={patientRelOptions}
          providerDisplayName={providerDisplayName}
          providerPhone={providerPhone}
          availabilityButtonsConfig={availabilityButtonsConfig}
          isPurposeLoading={isPurposeLoading}
          log={log}
          purposeHasNoAvailability={purposeHasNoAvailability}
          patientRelHasNoAvailability={patientRelHasNoAvailability}
          hidePatientRel={hidePatientRel}
          apptPurposeFilterProps={apptPurposeFilterProps}
          formName={apptInfoFormName}
        />
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="AvailabilityModal__cancel-btn"
          variant="text"
          color="primary"
          onClick={() => handleCloseModal('cancel_button')}
        >
          <FormattedMessage {...messages.availabilityModalCancel} />
        </Button>
        <Button
          data-testid="AvailabilityModal__confirm-btn"
          disabled={!purpose || purposeHasNoAvailability}
          variant="contained"
          color="primary"
          onClick={handleConfirmModal}
          type="submit"
          form={apptInfoFormName}
        >
          <FormattedMessage {...messages.availabilityModalConfirm} />
        </Button>
      </DialogActions>
    </AvailabilityDialog>
  );
}

ModalContainer.defaultProps = {
  patientRel: '',
  purpose: null,
  onPatientRelSelect: () => {},
  onSelectPurpose: () => {},
  isOpen: false,
  handleCloseModal: () => {},
  handleConfirmModal: () => {},
  patientRelOptions: {},
  hidePatientRel: false
};

ModalContainer.propTypes = {
  patientRel: PropTypes.string,
  purpose: PropTypes.object,
  onPatientRelSelect: PropTypes.func,
  onSelectPurpose: PropTypes.func,
  isOpen: PropTypes.bool,
  handleCloseModal: PropTypes.func,
  handleConfirmModal: PropTypes.func,
  patientRelOptions: PropTypes.object,
  hidePatientRel: PropTypes.bool,
  apptPurposeFilterProps: PropTypes.object
};

export default ModalContainer;
